import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthToken } from '../../hooks/useAuthToken';
import { useStoreContext } from '../../hooks/useStoreContext';
import { useToast } from './Toast';
import './PropertySelector.css';

const PropertySelector = ({ onSetupComplete, initialNoGAAccount = false }) => {
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPropertyName, setNewPropertyName] = useState('');
  const [error, setError] = useState(initialNoGAAccount ? { type: 'NO_GA_ACCOUNT', message: 'No Google Analytics account found.', link: 'https://analytics.google.com' } : null);
  
  const { token, isLoading: tokenLoading } = useAuthToken();
  const { storeName } = useStoreContext();
  const { showToast } = useToast();

  useEffect(() => {
    if (storeName) {
      setNewPropertyName(`${storeName} Analytics`);
    }
  }, [storeName]);

  useEffect(() => {
    if (token && !tokenLoading) {
      fetchProperties();
    }
  }, [token, tokenLoading]);

  const fetchProperties = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get business_id from localStorage
      const businessId = localStorage.getItem('user_id');
      
      if (!businessId) {
        setError('Business ID not found. Please log in again.');
        setIsLoading(false);
        return;
      }
      
      const response = await axios.get(`${process.env.REACT_APP_APIURL}analytics/properties`, {
        params: {
          business_id: businessId
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        if (response.data.data.connected === false) {
          setError('Google Analytics not connected');
          setIsLoading(false);
          return;
        }
        const props = response.data.data.properties || [];
        setProperties(props);
      }
    } catch (err) {
      console.error('Properties fetch error:', err);
      setError(err.response?.data?.message || 'Failed to load properties');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectProperty = async (property) => {
    try {
      setIsSubmitting(true);
      setSelectedPropertyId(property.propertyId);
      
      // Get business_id from localStorage
      const businessId = localStorage.getItem('user_id');
      if (!businessId) {
        showToast('Business ID not found. Please log in again.', 'error');
        setIsSubmitting(false);
        setSelectedPropertyId(null);
        return;
      }
      
      const response = await axios.post(`${process.env.REACT_APP_APIURL}analytics/property/select`, {
        business_id: businessId,
        propertyId: property.propertyId,
        accountId: property.accountId,
        propertyName: property.propertyName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        showToast('GA4 property connected!', 'success');
        onSetupComplete();
      }
    } catch (err) {
      console.error('Property select error:', err);
      showToast(err.response?.data?.message || 'Failed to select property', 'error');
    } finally {
      setIsSubmitting(false);
      setSelectedPropertyId(null);
    }
  };

  const handleCreateProperty = async () => {
    if (!newPropertyName.trim()) {
      showToast('Property name is required', 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      
      // Get business_id from localStorage
      const businessId = localStorage.getItem('user_id');
      if (!businessId) {
        showToast('Business ID not found. Please log in again.', 'error');
        setIsSubmitting(false);
        return;
      }
      
      // First create the property
      const createResponse = await axios.post(`${process.env.REACT_APP_APIURL}analytics/properties/create`, {
        business_id: businessId,
        propertyName: newPropertyName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (createResponse.data.success) {
        const { propertyId } = createResponse.data.data;
        
        // Then create the web stream
        const streamResponse = await axios.post(`${process.env.REACT_APP_APIURL}analytics/stream/create`, {
          business_id: businessId,
          propertyId,
          accountId: properties[0]?.accountId,
          propertyName: newPropertyName
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (streamResponse.data.success) {
          showToast('New GA4 property created!', 'success');
          onSetupComplete();
        }
      }
    } catch (err) {
      console.error('Property create error:', err);
      
      // Handle NO_GA_ACCOUNT error specifically
      if (err.response?.data?.code === 'NO_GA_ACCOUNT') {
        setError({ 
          type: 'NO_GA_ACCOUNT', 
          message: err.response.data.message, 
          link: err.response.data.link 
        });
        return;
      }
      
      // Handle generic errors
      const errorMessage = err.response?.data?.message || 'Failed to create property';
      setError({ 
        type: 'GENERIC', 
        message: errorMessage 
      });
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (error && typeof error === 'string') {
    return (
      <div className="ga-selector-wrapper">
        <div className="ga-error-banner">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="ga-selector-wrapper">
      <h2 className="ga-selector-title">Select your Google Analytics property</h2>
      <p className="ga-selector-sub">Choose where to send your store's visitor data</p>

      {isLoading ? (
        <div>
          {[1, 2, 3].map(i => (
            <div key={i} className="ga-property-card">
              <div>
                <div className="ga-skeleton" style={{ width: '200px', height: '16px', marginBottom: '4px' }}></div>
                <div className="ga-skeleton" style={{ width: '120px', height: '12px' }}></div>
              </div>
              <div className="ga-skeleton" style={{ width: '60px', height: '28px' }}></div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {properties.length > 0 && (
            <div>
              {properties.map(property => (
                <div key={property.propertyId} className="ga-property-card">
                  <div>
                    <div className="ga-property-name">{property.propertyName}</div>
                    <div className="ga-property-meta">{property.accountName}</div>
                    <div className="ga-property-id">{property.propertyId}</div>
                  </div>
                  <button
                    className="ga-select-btn"
                    onClick={() => handleSelectProperty(property)}
                    disabled={isSubmitting}
                  >
                    {isSubmitting && selectedPropertyId === property.propertyId ? (
                      <div className="ga-spinner"></div>
                    ) : (
                      'Select'
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="ga-divider-or">or</div>

          <div className="ga-create-section">
            <h3 className="ga-create-title">Create a new property</h3>
            
            {!showCreateForm ? (
              <button 
                className="ga-create-toggle-btn"
                onClick={() => setShowCreateForm(true)}
              >
                + Create new GA4 property for your store
              </button>
            ) : (
              <div>
                <input
                  type="text"
                  className="ga-input"
                  placeholder="Property name"
                  value={newPropertyName}
                  onChange={(e) => setNewPropertyName(e.target.value)}
                />
                <button
                  className="ga-create-submit-btn"
                  onClick={handleCreateProperty}
                  disabled={isSubmitting || !newPropertyName.trim()}
                >
                  {isSubmitting ? (
                    <div className="ga-spinner"></div>
                  ) : (
                    'Create & Connect'
                  )}
                </button>
              </div>
            )}
            
            {/* Show NO_GA_ACCOUNT error UI */}
            {error && error.type === 'NO_GA_ACCOUNT' && (
              <div style={{
                background: '#fffbeb',
                border: '1.5px solid #fcd34d',
                borderRadius: '12px',
                padding: '24px',
                textAlign: 'center',
                marginTop: '16px'
              }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>📊</div>
                <h4 style={{
                  color: '#92400e',
                  fontWeight: '600',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>
                  Google Analytics Account Required
                </h4>
                <p style={{
                  color: '#b45309',
                  fontSize: '14px',
                  marginBottom: '20px',
                  lineHeight: '1.6'
                }}>
                  You don't have a Google Analytics account yet. 
                  Create one for free at analytics.google.com — it only takes 1 minute.
                  Once done, come back and click "I've created my account" below.
                </p>
                <a
                  href="https://analytics.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    background: '#d97706',
                    color: 'white',
                    padding: '10px 28px',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: '500',
                    fontSize: '14px',
                    marginBottom: '12px'
                  }}
                >
                  Create Free GA4 Account →
                </a>
                <br />
                <button
                  onClick={() => { setError(null); setShowCreateForm(true); }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#b45309',
                    fontSize: '13px',
                    cursor: 'pointer',
                    marginTop: '8px',
                    textDecoration: 'underline'
                  }}
                >
                  I've created my account — try again
                </button>
              </div>
            )}
            
            {/* Show generic error */}
            {error && error.type === 'GENERIC' && (
              <div style={{
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: '8px',
                padding: '12px 16px',
                marginTop: '12px',
                color: '#DC2626',
                fontSize: '13px'
              }}>
                {error.message}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PropertySelector;